/**
 * Global error capture.
 * Listens to window.onerror and window.onunhandledrejection.
 * PII-scrubs error data before passing to the write callback.
 */

import type { CapturedError, NetworkFailure, RouteEntry } from '../types';
import { scrubPII } from '../pii';

export type ErrorCallback = (error: CapturedError) => void;

export interface ErrorCaptureContext {
  getConsoleEntries: () => string[];
  getRouteEntries: () => RouteEntry[];
  getNetworkFailures: () => NetworkFailure[];
}

export class ErrorCapture {
  private callback: ErrorCallback;
  private context: ErrorCaptureContext;
  private installed = false;
  private previousOnError: OnErrorEventHandler = null;
  private previousOnUnhandledRejection: ((event: PromiseRejectionEvent) => void) | null = null;
  private onErrorHandler: OnErrorEventHandlerNonNull | null = null;
  private onUnhandledRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

  constructor(callback: ErrorCallback, context: ErrorCaptureContext) {
    this.callback = callback;
    this.context = context;
  }

  /** Install global error listeners */
  install(): void {
    if (this.installed) return;
    if (typeof window === 'undefined') return;

    // Save previous handlers
    this.previousOnError = window.onerror;
    this.previousOnUnhandledRejection = window.onunhandledrejection as ((event: PromiseRejectionEvent) => void) | null;

    // Install window.onerror
    this.onErrorHandler = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error,
    ) => {
      const captured: CapturedError = {
        type: 'error',
        message: scrubPII(typeof message === 'string' ? message : message.type),
        stack: error?.stack ? scrubPII(error.stack) : null,
        url: source ? scrubPII(source) : null,
        line: lineno ?? null,
        column: colno ?? null,
        timestamp: new Date().toISOString(),
        consoleContext: this.context.getConsoleEntries().map(scrubPII),
        routeHistory: this.context.getRouteEntries(),
        recentNetworkFailures: this.context.getNetworkFailures(),
      };

      this.callback(captured);

      // Call previous handler if it existed
      if (typeof this.previousOnError === 'function') {
        this.previousOnError(message, source, lineno, colno, error);
      }
    };
    window.onerror = this.onErrorHandler;

    // Install unhandledrejection
    this.onUnhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection';
      const stack = reason instanceof Error ? reason.stack ?? null : null;

      const captured: CapturedError = {
        type: 'unhandled_rejection',
        message: scrubPII(message),
        stack: stack ? scrubPII(stack) : null,
        url: typeof window !== 'undefined' ? scrubPII(window.location.href) : null,
        line: null,
        column: null,
        timestamp: new Date().toISOString(),
        consoleContext: this.context.getConsoleEntries().map(scrubPII),
        routeHistory: this.context.getRouteEntries(),
        recentNetworkFailures: this.context.getNetworkFailures(),
      };

      this.callback(captured);

      // Call previous handler if it existed
      if (typeof this.previousOnUnhandledRejection === 'function') {
        this.previousOnUnhandledRejection(event);
      }
    };
    window.onunhandledrejection = this.onUnhandledRejectionHandler;

    this.installed = true;
  }

  /** Uninstall global error listeners */
  uninstall(): void {
    if (!this.installed) return;

    window.onerror = this.previousOnError;
    window.onunhandledrejection = this.previousOnUnhandledRejection as OnErrorEventHandler;
    this.previousOnError = null;
    this.previousOnUnhandledRejection = null;
    this.onErrorHandler = null;
    this.onUnhandledRejectionHandler = null;
    this.installed = false;
  }
}
